import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  useUser,
  useClerk,
  useAuth,
  RedirectToSignIn,
} from '@clerk/clerk-react';
import { Toaster } from 'react-hot-toast';
import { store } from './store/store';
import { useAppData } from './hooks/useAppData';
import Layout from './components/ui/Layout';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import PricingPage from './pages/PricingPage';
import PaymentTestPage from './pages/PaymentTestPage';
import PaymentVerification from './components/PaymentVerification';
import ErrorBoundary from './components/ErrorBoundary';
import EnhancedLandingNavigation from './components/EnhancedLandingNavigation';
import { useSelector, useDispatch } from 'react-redux';
import { setPaymentSuccess, setCurrentPage } from './store/slices/uiSlice';
import { setLoading as setAuthLoading } from './store/slices/authSlice';

import './App.css';

import { ConvexClientProvider } from './ConvexClientProvider';
import ConvexSubscriptionSync from './components/ConvexSubscriptionSync.jsx';
import DevPayments from './pages/DevPayments';
import FeatureTestPage from './pages/FeatureTestPage';
import PortalReturnSync from './components/PortalReturnSync.jsx';
import AutoPortalSync from './components/AutoPortalSync.jsx';

// App Content Component - handles the main application logic
const AppContent = () => {
  const dispatch = useDispatch();
  const { user, isSignedIn, isLoaded } = useUser();
  const { loadUserData } = useAppData();
  
  const { currentPage, paymentSuccess } = useSelector((state) => state.ui);
  const { loading: authLoading } = useSelector((state) => state.auth);

  // ALL HOOKS MUST BE CALLED AT THE TOP BEFORE ANY CONDITIONAL LOGIC
  
  // Track if we've already tried to load user data
  const [initialLoadAttempted, setInitialLoadAttempted] = React.useState(false);
  
  // Force bypass of loading screen immediately - simplified solution
  const [assumeNotSignedIn, setAssumeNotSignedIn] = React.useState(true); // Start as true

  // Initialize user data when signed in with aggressive timeout
  useEffect(() => {
    if (isSignedIn && isLoaded && !initialLoadAttempted) {
      console.log('🚀 App.js: Setting auth loading to false immediately (bypassing loadUserData until Convex is fixed)');
      setInitialLoadAttempted(true);
      
      // Immediately set loading to false instead of calling loadUserData
      dispatch(setAuthLoading(false));
    }
  }, [isSignedIn, isLoaded, initialLoadAttempted]);

  // Add fallback to prevent infinite loading if Clerk fails to load
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isLoaded) {
        console.warn('🚨 App.js: Clerk failed to load after 5 seconds, forcing app to continue');
        dispatch(setAuthLoading(false));
      }
    }, 5000); // Reduced from 8000

    return () => clearTimeout(timeout);
  }, [isLoaded, dispatch]);

  // Emergency loading protection - if loading state persists for any reason, force it off
  useEffect(() => {
    if (authLoading) {
      const emergencyTimeout = setTimeout(() => {
        console.warn('🚨 EMERGENCY: Forcing loading state off after 2 seconds');
        dispatch(setAuthLoading(false));
      }, 2000); // Reduced from 3000

      return () => clearTimeout(emergencyTimeout);
    }
  }, [authLoading, dispatch]);

  // Ultimate fail-safe: Force app to render after maximum 3 seconds total
  useEffect(() => {
    const ultimateTimeout = setTimeout(() => {
      console.warn('🚨 ULTIMATE FAIL-SAFE: Forcing app to render after 3 seconds');
      dispatch(setAuthLoading(false));
    }, 3000); // Reduced from 6000

    return () => clearTimeout(ultimateTimeout);
  }, [dispatch]);

  // Handle payment success callback
  const handlePaymentSuccess = async () => {
    dispatch(setPaymentSuccess(true));
    await loadUserData();
    setTimeout(() => {
      dispatch(setPaymentSuccess(false));
    }, 5000);
  };

  // Clean up payment-related URL parameters on app load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasPaymentParams = urlParams.has('payment') || 
                           urlParams.has('checkout') || 
                           urlParams.has('plan') || 
                           urlParams.has('customer_session_token') ||
                           urlParams.has('payment_success');
    
    if (hasPaymentParams) {
      console.log('🧹 Cleaning up payment URL parameters on app load...');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // If Clerk eventually loads and user is signed in, allow the app to continue
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      setAssumeNotSignedIn(false); // User is actually signed in
    }
  }, [isLoaded, isSignedIn]);

  // Fixed loading condition - prioritize timeout bypass flag over Clerk state
  const shouldShowLoading = !assumeNotSignedIn && (!isLoaded || (isSignedIn && authLoading));

  // Render payment verification component if needed
  // Instead of replacing the whole app with the verification overlay (which can trip ErrorBoundary),
  // render it non-blocking at the top level when URL indicates a post-payment return.
  const paymentSuccessFlag = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('payment_success') === 'true';
  const verificationOverlay = paymentSuccessFlag ? (
    <PaymentVerification onPaymentSuccess={handlePaymentSuccess} isPostPayment={true} />
  ) : null;
  
  // Handle return from Polar portal and sync subscription
  const onPortalSyncComplete = React.useCallback(() => {
    // No-op; PortalReturnSync handles URL cleanup already
  }, []);

  if (shouldShowLoading) {
    console.log(`🔍 Loading state debug: isLoaded=${isLoaded}, isSignedIn=${isSignedIn}, authLoading=${authLoading}`);
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading StreakKeeper...</p>
          <p className="mt-2 text-sm text-gray-400">
            {!isLoaded ? 'Initializing authentication...' : 'Loading your data...'}
          </p>
          <div className="mt-4 text-xs text-gray-400">
            Debug: Clerk={isLoaded ? '✅' : '❌'} | Signed In={isSignedIn ? '✅' : '❌'} | Loading={authLoading ? '🔄' : '✅'}
          </div>
        </div>
      </div>
    );
  }

  // Landing page for non-authenticated users (or when we assume not signed in)
  if (!isSignedIn || assumeNotSignedIn) {
    return (
      <div className="min-h-screen bg-gray-50">
        {verificationOverlay}
        <EnhancedLandingNavigation />
        {/* Auto sync when returning from portal */}
        <AutoPortalSync />
        {/* Backward compatibility: URL param-based sync */}
        <PortalReturnSync onComplete={onPortalSyncComplete} />
        {currentPage === 'pricing' ? <PricingPage /> : <LandingPage />}
      </div>
    );
  }

  const isDev = process.env.NODE_ENV !== 'production';

  // Dashboard for authenticated users
  return (
    <Layout>
      {verificationOverlay}
      {/* Auto sync when returning from portal */}
      <AutoPortalSync />
      {/* Backward compatibility: URL param-based sync */}
      <PortalReturnSync onComplete={onPortalSyncComplete} />

      {/* Payment Success Banner */}
      {paymentSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">Payment successful! Your subscription is now active and GitHub integration is enabled.</span>
          </div>
        </div>
      )}

      {/* Page Content */}
      {isDev && currentPage === 'devpayments' ? (
        <DevPayments />
      ) : isDev && currentPage === 'feature-test' ? (
        <FeatureTestPage />
      ) : isDev && currentPage === 'payment-test' ? (
        <PaymentTestPage />
      ) : currentPage === 'settings' ? (
        <SettingsPage />
      ) : (
        <DashboardPage />
      )}
    </Layout>
  );
};

// Main App Component with Redux Provider and Clerk Provider
const App = () => {
  const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || "pk_test_YmFsYW5jZWQtY29yZ2ktOTAuY2xlcmsuYWNjb3VudHMuZGV2JA";

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ClerkProvider publishableKey={clerkPubKey}>
          {/* Convex provider wraps the entire app to enable queries/mutations later */}
          <ConvexClientProvider>
            <div className="App">
              <AppContent />
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 4000,
                    style: {
                      background: '#10b981',
                      color: '#fff',
                    },
                  },
                  error: {
                    duration: 5000,
                    style: {
                      background: '#ef4444',
                      color: '#fff',
                    },
                  },
                }}
              />
            </div>
          </ConvexClientProvider>
        </ClerkProvider>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
